import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENTS = ["#d4af37", "#8b5cf6", "#22d3ee", "#ef4444", "#10b981", "#f97316", "#ec4899", "#3b82f6"];

export default function SettingsPage({ user }) {
  const [form, setForm] = useState({
    name: user.name || "",
    handle: user.handle || "",
    bio: user.bio || "",
    picture: user.picture || "",
    avantlink_affiliate_id: user.avantlink_affiliate_id || "",
    avantlink_website_id: user.avantlink_website_id || "",
    theme: user.theme || "dark",
    accent_color: user.accent_color || "#d4af37",
    seo_title: user.seo_title || "",
    seo_description: user.seo_description || "",
  });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings", form);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const testAvantlink = async () => {
    setTestResult(null);
    try {
      const r = await api.get("/avantlink/test?search_term=shoes");
      setTestResult({ ok: r.data.status_code === 200, ...r.data });
    } catch (err) {
      setTestResult({ ok: false, error: err?.response?.data?.detail || "failed" });
    }
  };

  return (
    <div className="p-10 max-w-3xl">
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Configure</div>
        <h1 className="font-heading font-light text-4xl tracking-tighter mt-2">Settings</h1>
      </div>

      <form onSubmit={save} className="space-y-8">
        <section className="border border-zinc-200 bg-white p-6 space-y-4">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Public profile</div>
          <Field label="Display name" testid="settings-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Handle" testid="settings-handle" prefix="/u/" value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} />
          <Field label="Avatar URL" testid="settings-picture" value={form.picture} onChange={(v) => setForm({ ...form, picture: v })} />
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">Bio</label>
            <textarea data-testid="settings-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
              className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm" />
          </div>
        </section>

        <section className="border border-zinc-200 bg-white p-6 space-y-5">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Public page theme</div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-3">Theme</label>
            <div className="flex gap-3">
              {[
                { k: "dark", label: "Editorial Dark", preview: "bg-zinc-950 text-zinc-100 border-zinc-800" },
                { k: "light", label: "Swiss Light", preview: "bg-stone-50 text-zinc-950 border-zinc-200" },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  data-testid={`theme-${t.k}`}
                  onClick={() => setForm({ ...form, theme: t.k })}
                  className={`flex-1 border-2 p-5 text-left transition-colors ${form.theme === t.k ? "border-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  <div className={`w-full h-16 border rounded-sm mb-3 ${t.preview} flex items-center justify-center font-heading tracking-tighter text-lg`}>Aa</div>
                  <div className="text-sm font-medium">{t.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-3">Accent color</label>
            <div className="flex flex-wrap gap-2">
              {ACCENTS.map((c) => (
                <button key={c} type="button" data-testid={`accent-${c}`}
                  onClick={() => setForm({ ...form, accent_color: c })}
                  style={{ background: c }}
                  className={`w-9 h-9 rounded-full transition-transform ${form.accent_color === c ? "ring-2 ring-offset-2 ring-zinc-950 scale-110" : "hover:scale-110"}`}
                  aria-label={c} />
              ))}
              <input
                data-testid="accent-custom"
                type="color"
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                className="w-9 h-9 rounded-full border-0 cursor-pointer bg-transparent"
              />
            </div>
          </div>
        </section>

        <section className="border border-zinc-200 bg-white p-6 space-y-4">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Search engine (SEO)</div>
          <p className="text-sm text-zinc-600">Control exactly what Google shows for your public page. Leave blank to auto-generate from your name, bio and categories.</p>
          <Field label="Page title" testid="settings-seo-title" value={form.seo_title} onChange={(v) => setForm({ ...form, seo_title: v })} />
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">Meta description</label>
            <textarea data-testid="settings-seo-description" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} rows={2} maxLength={200}
              className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm" />
            <div className="text-xs text-zinc-400 mt-1 font-mono">{form.seo_description.length}/200</div>
          </div>
        </section>

        <section className="border border-zinc-200 bg-white p-6 space-y-4">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">AvantLink</div>
          <p className="text-sm text-zinc-600">
            Enter your AvantLink Affiliate ID and Website ID from <a href="https://classic.avantlink.com" target="_blank" rel="noreferrer" className="underline">AvantLink</a> → Tools → API Request Builder.
          </p>
          <Field label="Affiliate ID" testid="settings-affid" value={form.avantlink_affiliate_id} onChange={(v) => setForm({ ...form, avantlink_affiliate_id: v })} />
          <Field label="Website ID" testid="settings-wid" value={form.avantlink_website_id} onChange={(v) => setForm({ ...form, avantlink_website_id: v })} />
          <button type="button" data-testid="test-avantlink-btn" onClick={testAvantlink}
            className="text-xs border border-zinc-200 hover:bg-zinc-50 px-3 py-2 rounded-sm">Test connection</button>
          {testResult && (
            <div data-testid="avantlink-test-result" className={`text-xs font-mono p-3 rounded-sm ${testResult.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`}>
              {testResult.ok ? "✓ API responded" : "✗ Test failed"}
              {testResult.sample && <div className="mt-1 text-zinc-600 line-clamp-3 break-all">{testResult.sample}</div>}
              {testResult.error && <div className="mt-1">{testResult.error}</div>}
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <button type="submit" data-testid="settings-save-btn" disabled={busy}
            className="bg-zinc-950 text-white text-sm px-6 py-2.5 hover:bg-zinc-800 disabled:opacity-50 rounded-sm">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, prefix }) {
  return (
    <div>
      <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">{label}</label>
      <div className="flex">
        {prefix && <span className="inline-flex items-center px-3 border border-r-0 border-zinc-200 bg-zinc-50 text-sm font-mono text-zinc-500 rounded-l-sm">{prefix}</span>}
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
          className={`flex-1 text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 ${prefix ? "rounded-r-sm" : "rounded-sm"}`} />
      </div>
    </div>
  );
}
